import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';

import { updateCapacityAgreement } from '../apiService';
import { SLOT_DETAILS_MAX_LENGTH, SLOT_DETAILS_MAX_ROWS } from '../constants';
import { TEXTS } from '../texts';
import type { CapacitySlotType, CapacitySlotUpdateJsonType } from '../types';

interface EditCapacitySlotInnerProps {
  open: boolean;
  onClose: () => void;
}

interface EditCapacitySlotProps {
  open: boolean;
  onClose: () => void;
  onUpdateSuccess?: () => Promise<void> | void;
  capacitySlot: CapacitySlotType;
  capacityAgreementExternalId: string;
  policyId: number;
  isManualPolicy: boolean;
  organizationId: number;
}

const validationSchema = Yup.object().shape({
  slot_name: Yup.string().nullable().trim().required(TEXTS.required),
  details: Yup.string().nullable().trim().max(SLOT_DETAILS_MAX_LENGTH, TEXTS.slotDetailsMaxLength),
});

const getInitialValues = (capacitySlot: CapacitySlotType): CapacitySlotUpdateJsonType => {
  return {
    external_id: capacitySlot.external_id,
    slot_name: capacitySlot.slot_name,
    details: capacitySlot.details ?? '',
  };
};

const EditCapacitySlotInner = ({ open, onClose }: EditCapacitySlotInnerProps) => {
  const { isSubmitting } = useFormikContext();
  return (
    <CardDialog isDialog open={open} onClose={onClose} title={TEXTS.editSlotDialogTitle()} fullWidth maxWidth="sm">
      <Form className="grid gap-24">
        <div className="grid grid-cols-2 items-end gap-24">
          <TextFieldFormik id="slot_name" label={TEXTS.slotsFieldLabels.slot_name} />
          <TextFieldFormik
            id="details"
            label={TEXTS.slotsFieldLabels.details}
            multiline
            maxRows={SLOT_DETAILS_MAX_ROWS}
          />
        </div>
        <div className="justify-self-end">
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={onClose}
            className="mr-16"
            disabled={isSubmitting}
          >
            {TEXTS.cancel}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {TEXTS.submit}
          </Button>
        </div>
      </Form>
    </CardDialog>
  );
};

function EditCapacitySlotDialog({
  open: openFromProps,
  onClose,
  onUpdateSuccess,
  capacitySlot,
  capacityAgreementExternalId,
  policyId,
  isManualPolicy,
  organizationId,
}: EditCapacitySlotProps) {
  const open = !!(openFromProps && capacitySlot && capacityAgreementExternalId);
  const initialValues = getInitialValues(capacitySlot);

  const update = async (values: CapacitySlotUpdateJsonType) => {
    if (!open) {
      return;
    }
    const json = {
      external_id: capacityAgreementExternalId,
      // `details` is a nullable column, so send an explicit `null` for a blank field rather than
      // leaning on the server coercing `''` to None. Trim first, matching what the server stores.
      capacity_slots_updates: [{ ...values, details: values.details?.trim() || null }],
    };
    let isApiSuccess = false;
    try {
      await updateCapacityAgreement({ organizationId, policyId, json, isManualPolicy });
      isApiSuccess = true;
      if (onUpdateSuccess) {
        await onUpdateSuccess();
      }
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      if (isApiSuccess) {
        onClose?.();
      }
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={update} enableReinitialize>
      <EditCapacitySlotInner open={open} onClose={onClose} />
    </Formik>
  );
}

export default EditCapacitySlotDialog;
